import { type OperationId } from "@gen/app-access-config";
import {
  CoreQuery,
  type OnlineTransactionStatus,
  OnlineTransactionStatusCode,
  Query,
} from "@gen/app-query";
import { onlineTransactionStatusConfig } from "@gen/transaction-status-config";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useParams } from "@tanstack/react-router";
import { useMemo } from "react";

import { LoadingScreen } from "~/components/loading_screen";
import { PublicSvgIcon } from "~/components/public_svg_icon";
import {
  SectionSideNav,
  type SectionSideNavItem,
} from "~/components/section_side_nav";
import { StatusChip } from "~/components/status_chip";
import { useAuthOperations } from "~/contexts/auth_query";
import { TransactionMoney } from "~/entities/transaction";

import { BuyerLedger } from "../BuyerLedger";
import { ExtrasSection } from "../ExtrasSection";
import { SellerLedger } from "../SellerLedger";
import { TransactionTimelineSection } from "../TransactionTimeline";
import { BuyerActionsSection } from "./BuyerActionsSection";
import { NoteSection } from "./NoteSection";
import { PaymentReviewSection } from "./PaymentReviewSection";
import { SellerActionsSection } from "./SellerActionsSection";
import { UserDetails } from "./UserDetails";

interface StripePlatform {
  id: string;
  label: string;
}

const isOnlineStatusCode = (
  status: string | undefined,
  statuses?: OnlineTransactionStatus[],
): status is OnlineTransactionStatusCode =>
  Boolean(status && statuses?.some((s) => s.code === status));

export function OnlineTransaction() {
  const { canExecuteAdminOperation } = useAuthOperations();

  const onlineTransactionId = useParams({
    from: "/admin/online/transactions/$transactionId",
    select: (params) => Number(params.transactionId),
    shouldThrow: true,
  });

  const {
    data: onlineTransactionData,
    isFetching: isFetchingOnlineTransaction,
  } = CoreQuery.useAdmin_getOnlineTransactionQuery(onlineTransactionId, {
    enabled: onlineTransactionId !== undefined,
  });

  const stripePlatformId = onlineTransactionData?.stripe_platform_id ?? "";
  const paymentIntentId = onlineTransactionData?.stripe_payment_intent_id ?? "";

  const isPaymentInReview = onlineTransactionData?.payment_in_review;

  const sideNavItems: SectionSideNavItem[] = useMemo(() => {
    const items: SectionSideNavItem[] = [
      { id: "summary", label: "Transaction Details" },
    ];
    if (isPaymentInReview) {
      items.push({ id: "payment-review", label: "Under Review" });
    }
    items.push(
      { id: "buyer", label: "Buyer Overview" },
      { id: "seller", label: "Seller Overview" },
      { id: "notes", label: "Note" },
      { id: "timeline", label: "Timeline" },
      { id: "client", label: "Client" },
      { id: "extras", label: "Extras" },
    );
    return items;
  }, [isPaymentInReview]);
  const { data: stripePaymentIntentData } =
    CoreQuery.useAdmin_getStripePaymentIntentQuery(
      {
        stripe_platform_id: stripePlatformId,
        payment_intent_id: paymentIntentId,
      },
      {
        enabled: !!paymentIntentId && !!stripePlatformId,
      },
    );

  const { data: statusData } = Query.useCore_getTransactionStatusesQuery();
  const statusMap = new Map((statusData?.online ?? []).map((s) => [s.code, s]));

  const { data: stripePlatformsData } =
    CoreQuery.useAdmin_getStripePlatformsQuery();

  const canExecuteForStatus = useMemo(() => {
    return (operationId: OperationId): boolean => {
      if (!onlineTransactionData?.status) {
        return false;
      }

      const allowedOperations: readonly OperationId[] =
        onlineTransactionStatusConfig[onlineTransactionData.status] || [];

      return (
        allowedOperations.includes(operationId) &&
        canExecuteAdminOperation(operationId)
      );
    };
  }, [onlineTransactionData?.status, canExecuteAdminOperation]);

  const stripePlatformName = useMemo(() => {
    if (
      !onlineTransactionData?.stripe_platform_id ||
      !stripePlatformsData?.stripe_platforms
    ) {
      return "N/A";
    }
    const platform = stripePlatformsData.stripe_platforms.find(
      (sp: StripePlatform) =>
        sp.id === onlineTransactionData.stripe_platform_id,
    );
    return platform?.label || onlineTransactionData.stripe_platform_id;
  }, [
    onlineTransactionData?.stripe_platform_id,
    stripePlatformsData?.stripe_platforms,
  ]);

  if (isFetchingOnlineTransaction) {
    return <LoadingScreen />;
  }

  if (!onlineTransactionData || !onlineTransactionId) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ p: 4 }}>
        <Typography variant="body1">Transaction not found.</Typography>
      </Stack>
    );
  }

  const transactionMoney = new TransactionMoney(onlineTransactionData);
  const statusKey = isOnlineStatusCode(
    onlineTransactionData.status,
    statusData?.online,
  )
    ? onlineTransactionData.status
    : undefined;
  const statusInfo = statusKey ? statusMap.get(statusKey) : undefined;

  const shouldShowStripePlatform =
    onlineTransactionData.status !== OnlineTransactionStatusCode.Created &&
    onlineTransactionData.status !== OnlineTransactionStatusCode.Joined;

  return (
    <Box
      sx={{
        display: { xs: "block", md: "flex" },
        gap: { lg: 4.5 },
        alignItems: "flex-start",
      }}
    >
      <Box
        sx={{
          display: { xs: "contents", md: "block" },
          order: { md: 1 },
          flex: { md: "0 0 200px" },
          minWidth: 0,
          alignSelf: "stretch",
        }}
      >
        <SectionSideNav items={sideNavItems} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack
          id="summary"
          flexDirection={{ xs: "column", lg: "row" }}
          spacing={{ xs: 2, lg: 5 }}
          sx={{
            mb: { xs: 3, lg: 5 },
            p: { xs: 2, sm: 3, lg: 6 },
            backgroundColor: "primary._states.hover",
            borderRadius: 2,
            scrollMarginTop: (theme) => theme.spacing(10),
          }}
          useFlexGap
        >
          <Stack flexDirection="column" flexGrow={1}>
            <Typography variant="body1DashedDivider">Client</Typography>
            <Typography variant="body2" data-testid="transaction-client-text">
              {onlineTransactionData.client_site || "N/A"}
            </Typography>
          </Stack>
          <Stack flexDirection="column" flexGrow={1}>
            <Typography variant="body1DashedDivider">
              Transaction total
            </Typography>
            <Typography variant="body2" data-testid="transaction-total-text">
              {transactionMoney.getTotal().format() || "N/A"}
            </Typography>
          </Stack>
          <Stack flexDirection="column" flexGrow={1}>
            <Typography variant="body1DashedDivider">Description</Typography>
            <Typography
              variant="body2"
              data-testid="transaction-description-text"
            >
              {onlineTransactionData.descr || "N/A"}
            </Typography>
          </Stack>
          <Stack flexDirection="column" flexGrow={1}>
            <Typography variant="body1DashedDivider">Status</Typography>
            {statusInfo ? (
              <StatusChip
                label={statusInfo.label}
                color={statusInfo.color}
                dataTestId="transaction-status-text"
              />
            ) : (
              <Typography variant="body2" data-testid="transaction-status-text">
                {onlineTransactionData.status}
              </Typography>
            )}
          </Stack>
          {shouldShowStripePlatform && (
            <Stack flexDirection="column" flexGrow={1}>
              <Typography variant="body1DashedDivider">
                Stripe Platform
              </Typography>
              <Typography
                variant="body2"
                data-testid="transaction-stripe-platform-text"
              >
                {stripePlatformName}
              </Typography>
            </Stack>
          )}
        </Stack>
        {isPaymentInReview && (
          <Box
            id="payment-review"
            sx={{ scrollMarginTop: (theme) => theme.spacing(10) }}
          >
            <PaymentReviewSection
              transactionId={onlineTransactionId}
              isPaymentInReview={isPaymentInReview}
            />
          </Box>
        )}
        <Grid
          container
          spacing={{ xs: 3, lg: 6 }}
          sx={{
            mb: { xs: 3, lg: 5 },
            backgroundColor: "primary._states.hover",
            borderRadius: 2,
            height: "max-content",
            p: { xs: 2, lg: 0 },
          }}
        >
          <Grid
            sx={{
              p: { xs: 0, lg: 6 },
              scrollMarginTop: (theme) => theme.spacing(10),
            }}
            size={{ xs: 12, lg: 6 }}
            id="buyer"
          >
            <Stack spacing={5}>
              <Typography
                variant="h5SolidDivider"
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <PublicSvgIcon name="shopping_cart" /> Buyer overview
              </Typography>
              <UserDetails
                stripeCustomerId={stripePaymentIntentData?.customer_id ?? ""}
                transactionData={onlineTransactionData}
                userTransactionRole="buyer"
              />
              <BuyerLedger transactionData={onlineTransactionData} />
              <BuyerActionsSection
                canExecuteForStatus={canExecuteForStatus}
                transactionData={onlineTransactionData}
              />
            </Stack>
          </Grid>

          <Grid
            sx={{
              p: { xs: 0, lg: 6 },
              scrollMarginTop: (theme) => theme.spacing(10),
            }}
            size={{ xs: 12, lg: 6 }}
            id="seller"
          >
            <Stack spacing={{ xs: 3, lg: 5 }}>
              <Typography
                variant="h5SolidDivider"
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <PublicSvgIcon name="storefront" /> Seller overview
              </Typography>
              <UserDetails
                transactionData={onlineTransactionData}
                userTransactionRole="seller"
              />
              <SellerLedger transactionData={onlineTransactionData} />
              <SellerActionsSection
                transactionData={onlineTransactionData}
                canExecuteForStatus={canExecuteForStatus}
              />
            </Stack>
          </Grid>
        </Grid>
        <Stack spacing={{ xs: 3, lg: 5 }}>
          <Box
            id="notes"
            sx={{ scrollMarginTop: (theme) => theme.spacing(10) }}
          >
            <NoteSection transactionData={onlineTransactionData} />
          </Box>
          <Box
            id="timeline"
            sx={{ scrollMarginTop: (theme) => theme.spacing(10) }}
          >
            <TransactionTimelineSection
              timeline={onlineTransactionData.timeline || []}
              statusData={statusData}
            />
          </Box>
          <Box
            id="extras"
            sx={{ scrollMarginTop: (theme) => theme.spacing(10) }}
          >
            <ExtrasSection
              transactionData={onlineTransactionData}
              transactionId={onlineTransactionId}
            />
          </Box>
        </Stack>
      </Box>
    </Box>
  );
}
